import React, { useCallback, useMemo, useState } from "react";

// Servisni sloj
import { DataWithPagination } from "services/api";
import { getFleetVehicleDriversAPI } from "services/api/fleet/vehicles/drivers/api";
import { FleetVehicleDriverRow } from "services/api/fleet/vehicles/drivers/types";
import { initialDataWithPagination } from "services/api/consts";
import {
  DropdownCacheKey,
  buildDropdownCacheKey,
  invalidateDropdownCache,
  useDropdownCache,
} from "services/hooks/useDropdownCache";

// UI
import { Button } from "ui/components/Button/Button";
import VehicleDriverEditCard from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleDriverEditCard/VehicleDriverEditCard";

import { DropdownAutocomplete } from "../DropdownAutocomplete";
import { DropdownOption } from "../Dropdown/types";
import { DriverDropdownProps } from "./types";

/** Vozilo rijetko ima više od nekoliko vozača, pa se dohvaćaju odjednom. */
const DRIVER_PAGE_SIZE = 100;

/** Novi vozač mora biti vidljiv u svim dropdownima, ne samo u onom iz kojeg je unesen. */
export const invalidateDrivers = () => invalidateDropdownCache(DropdownCacheKey.Drivers);

const toDriverOption = (driver: FleetVehicleDriverRow): DropdownOption => ({
  value: driver.id,
  text: (
    <div className="flex items-center justify-between w-full">
      <span>{driver.name ?? "Vozač"}</span>
      <span className="text-xs bg-blue-200 text-blue-900 py-0.5 px-2 rounded-sm block ml-2.5">
        {driver.userId ? "Korisnik" : "Kontakt"}
      </span>
    </div>
  ),
});

/**
 * Odabir vozača vozila. Iza vozača je korisnik aplikacije ili kontakt iz imenika, ali to
 * je odluka koja se donosi pri unosu vozača — ovdje se bira gotov vozač, kao što se
 * dobavljač bira iz kontakata.
 */
const DriverDropdown: React.FC<DriverDropdownProps> = (props) => {
  const {
    name,
    disabled,
    required,
    label,
    error,
    value,
    width,
    placeholder,
    vehicleId,
    onChange,
  } = props;

  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);

  const cacheKey = buildDropdownCacheKey(DropdownCacheKey.Drivers, vehicleId);

  const { data, isLoading } = useDropdownCache<DataWithPagination<FleetVehicleDriverRow>>(
    cacheKey,
    () =>
      getFleetVehicleDriversAPI(
        { ...initialDataWithPagination.pagination, pageSize: DRIVER_PAGE_SIZE },
        vehicleId
      ),
    5 * 60 * 1000,
    !!vehicleId
  );

  const options: DropdownOption[] = useMemo(
    () => (data?.data ?? []).map(toDriverOption),
    [data]
  );

  const handleSearchFocus = useCallback(() => undefined, []);
  const handleSearchChange = useCallback(() => undefined, []);

  const openAddModal = useCallback((e: any) => {
    e.preventDefault();
    setShowAddNewModal(true);
  }, []);

  const handleAddModalClose = useCallback(() => {
    setShowAddNewModal(false);
  }, []);

  // Novi vozač se odmah odabire — zbog toga je unos i otvoren.
  const handleEditSuccess = useCallback(
    (driverId?: string) => {
      invalidateDrivers();
      setShowAddNewModal(false);

      if (driverId && onChange) {
        onChange({ target: { name, value: driverId } } as any, name, {
          value: driverId,
          text: "",
        });
      }
    },
    [name, onChange]
  );

  return (
    <>
      <div className="flex items-end">
        <div className="flex-1">
          <DropdownAutocomplete
            label={label}
            name={name}
            disabled={disabled}
            required={required}
            placeholder={placeholder}
            value={value}
            width={width}
            options={options}
            onChange={onChange}
            onSearchChange={handleSearchChange}
            onSearchFocus={handleSearchFocus}
            error={error}
            isLoading={isLoading}
            inputClassName={!disabled ? "!rounded-r-none" : undefined}
          />
        </div>
        {!disabled && (
          <Button
            variant="outlined"
            startIcon="/media/icons/plus.svg"
            onClick={openAddModal}
            disabled={disabled}
            // Prati vanjski rub polja: visina polja je u rem, a njegov ring-1 je izvan okvira (+1 px gore i
            // dolje), dok je ring ovog gumba unutra. Bez toga gumb ispadne niži od polja.
            className="h-[calc(2.75rem+2px)] -mb-px"
          />
        )}
      </div>

      {/* Unos vozača je čarobnjak s vlastitim modalom — ovdje se samo otvara. Dropdown
          često stoji u drugom modalu, pa ide manji centrirani prozor bez uvoda. */}
      {showAddNewModal && (
        <VehicleDriverEditCard
          show={showAddNewModal}
          vehicleId={vehicleId}
          driverId=""
          isFullScreen={false}
          onSuccess={handleEditSuccess}
          onClose={handleAddModalClose}
        />
      )}
    </>
  );
};

export default React.memo(DriverDropdown);
